import React, { useEffect, useState } from "react";
import { GoogleLogin } from '@react-oauth/google';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import YellowBackButton from '../infrastructure/sharedComponents/yellowBackButton.js';
import './auth.component.loginWithGoogle.scss';
import useLastSkillTracking from '../hooks/useLastSkillTracking.js';
import { useAppTheme, useThemeHelpers } from '../../hooks/useAppTheme.js';
import '../../styles/loginWithGoogle.big-top.scss';
import '../../styles/teacherOnboarding.big-top.scss';

import { config } from '../constants.js';
import {
  isRememberMeEligible,
  setRememberMeEligible,
} from '../infrastructure/authStorage.js';
import {
  getStoredReferralUsername,
  clearStoredReferralUsername,
} from '../../utils/referralStorage.js';
import { completeSessionAfterAuth, getUserTimezone } from '../../utils/authSession.js';
import { resolveAuthReturnPath } from '../../utils/authRedirect.js';
import { hasPendingGuestSkillClaim } from '../../utils/pendingGuestSkillClaim.js';
import { usePaywall } from '../paywall/PaywallProvider.js';
import {
  buildAuthNavigationState,
  isJoinCheckoutIntent,
} from '../paywall/joinCheckoutFlow.js';

var url = config.url.API_URL;

const MOBILE_GOOGLE_BTN_WIDTH_RATIO = 0.9;
const MOBILE_GOOGLE_BTN_CONTENT_SCALE = 1.5;
const GOOGLE_BTN_MIN_WIDTH = 240;
const GOOGLE_BTN_MAX_WIDTH = 400;
const GOOGLE_BTN_LARGE_HEIGHT = 40;

function getMobileGoogleBtnWidth(viewportWidth) {
    return Math.min(
        GOOGLE_BTN_MAX_WIDTH,
        Math.max(GOOGLE_BTN_MIN_WIDTH, Math.floor(viewportWidth * MOBILE_GOOGLE_BTN_WIDTH_RATIO)),
    );
}

function getMobileGoogleBtnLayout(viewportWidth) {
    const visualWidth = getMobileGoogleBtnWidth(viewportWidth);
    const renderWidth = Math.min(
        GOOGLE_BTN_MAX_WIDTH,
        Math.max(
            GOOGLE_BTN_MIN_WIDTH,
            Math.floor(visualWidth / MOBILE_GOOGLE_BTN_CONTENT_SCALE),
        ),
    );

    return {
        renderWidth,
        scale: visualWidth / renderWidth,
    };
}

export default function LoginWithGoogle({setUserEmail, setIsAuthenticated, setUserId}) {
    const { getLastSkill, findNextLearningOpportunity } = useLastSkillTracking(null);
    const theme = useAppTheme();
    const { isThemed, pageClass, appContentClass } = useThemeHelpers(theme);
    const pageThemeClass = pageClass('loginWithGoogle');
    const shellClass = pageThemeClass ? `login-with-google ${pageThemeClass}` : undefined;
    const legacyStyle = (style) => (isThemed ? undefined : style);
    
    const [status, setStatus] = useState({ message: "", type: ""});
    const [loading, setLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [showRememberMe, setShowRememberMe] = useState(() => isRememberMeEligible());
    const [accountIntent, setAccountIntent] = useState('student');
    const [googleBtnLayout, setGoogleBtnLayout] = useState(null);

    const location = useLocation();
    const navigate = useNavigate();
    const from = resolveAuthReturnPath(location);
    const intent = location.state?.intent;
    const authNavState = buildAuthNavigationState(from, intent);
    const isJoinFlow = isJoinCheckoutIntent(intent);
    const { refreshAccess } = usePaywall();
    const sessionMessage = location.state?.message;
    const showClaimReinforcement = Boolean(location.state?.claimAfterAuth) || hasPendingGuestSkillClaim();

    let loginTimeout;

    useEffect(() => {
        if (sessionMessage) {
            setStatus({ message: sessionMessage, type: "warning" });
        }
    }, [sessionMessage]);

    useEffect(() => {
        if (!isThemed || !appContentClass) {
            return undefined;
        }

        const appContent = document.querySelector('.appContent');
        appContent?.classList.add(appContentClass);

        return () => {
            appContent?.classList.remove(appContentClass);
        };
    }, [isThemed, appContentClass]);

    useEffect(() => {
        if (!isThemed) {
            return undefined;
        }

        const mobileQuery = window.matchMedia('(max-width: 600px)');

        const updateGoogleBtnWidth = () => {
            setGoogleBtnLayout(
                mobileQuery.matches ? getMobileGoogleBtnLayout(window.innerWidth) : null,
            );
        };

        updateGoogleBtnWidth();
        mobileQuery.addEventListener('change', updateGoogleBtnWidth);
        window.addEventListener('resize', updateGoogleBtnWidth);

        return () => {
            mobileQuery.removeEventListener('change', updateGoogleBtnWidth);
            window.removeEventListener('resize', updateGoogleBtnWidth);
        };
    }, [isThemed]);

    const handleLoginSuccess = (response) => {
        clearTimeout(loginTimeout);
        loginTimeout = setTimeout(async () => {
            setLoading(true);
            const { credential } = response;

            try {
                const res = await fetch(`${url}/record/googleLogin`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        token: credential,
                        timezone: getUserTimezone(),
                        rememberMe,
                        accountIntent,
                        referralUsername: getStoredReferralUsername(),
                    }),
                    credentials: 'include',
                });
                const data = await res.json();

                if (!res.ok) {
                    throw new Error(data.message || data.error || 'Login failed. Please try again.');
                }

                setStatus({ message: "Login successful!", type: "success" });
                setRememberMeEligible(Boolean(data.access?.canUseRememberMe));
                setShowRememberMe(Boolean(data.access?.canUseRememberMe));
                if (!data.access?.canUseRememberMe) {
                    setRememberMe(false);
                }

                await completeSessionAfterAuth({
                    authResponse: data,
                    navigate,
                    from,
                    intent,
                    refreshAccess,
                    setUserEmail,
                    setIsAuthenticated,
                    setUserId,
                    getLastSkill,
                    findNextLearningOpportunity,
                });
            } catch (err) {
                console.error('Backend Verification Error:', err);
                setStatus({
                    message: err.message || "Login failed. Please try again.",
                    type: "error",
                });
            } finally {
                setLoading(false);
            }
        }, 300);
    };

    const handleLoginError = (error) => {
        console.error('Google Login Error:', error);
        const message = error?.details || "An error occurred during login. Please try again.";
        setStatus({ message, type: "error" });
    };

    const styleTitle = {
        fontFamily: "Montserrat",
        fontWeight: "600",
        fontSize: '4rem',
        textAlign: "center",
        paddingBottom: "5%",
        color: "var(--golden-yellow)",
        textShadow: "2.5px 2.5px 0px #555"
    };

    return (
        <div className={shellClass ?? 'col-12'}>
            <div className={isThemed ? 'login-with-google__back-row' : 'row'}>
                <div className={isThemed ? undefined : 'col-12 d-flex justify-content-start p-5'}>
                    <YellowBackButton
                        onClick={() => navigate('/')}
                        title="Back to Home"
                        variant={isThemed ? 'pathway-ink' : 'legacy'}
                    />
                </div>
            </div>
            { status.message && (
                <div className={isThemed ? 'login-with-google__alert-wrap' : 'row'}>
                    <div
                        className={isThemed
                            ? `login-with-google__alert login-with-google__alert--${status.type}`
                            : `alert alert-${status.type}`}
                        role="alert"
                    >
                        { status.message }
                    </div>
                </div>
            )}
            <div className={isThemed ? undefined : 'row'}>
                <p
                    className={isThemed ? 'login-with-google__title' : undefined}
                    style={legacyStyle(styleTitle)}
                >
                    Calculus Circus
                </p>
            </div>
            <div className={isThemed ? undefined : 'row'}>
                <div className={isThemed ? 'login-with-google__login-wrap' : 'd-flex justify-content-center mb-5'}>
                    <div className="login-with-google__login-stack">
                        {showClaimReinforcement && (
                            <p
                                className={isThemed ? 'login-with-google__claim-reinforcement' : undefined}
                                style={!isThemed ? {
                                    textAlign: 'center',
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    maxWidth: '22rem',
                                    margin: 0,
                                    lineHeight: 1.4,
                                    color: '#333',
                                } : undefined}
                            >
                                Completing sign-in will save this skill and add your tickets.
                            </p>
                        )}
                        {loading ? (
                            <div
                                className={isThemed ? 'login-with-google__spinner spinner-border' : 'spinner-border'}
                                role="status"
                            >
                                <span className="sr-only">Loading . . .</span>
                            </div>
                        ) : (
                            <div
                                className={isThemed ? 'login-with-google__google-wrap' : undefined}
                                style={isThemed && googleBtnLayout
                                    ? {
                                        '--google-btn-content-scale': googleBtnLayout.scale,
                                        '--google-btn-scale-gutter': `${GOOGLE_BTN_LARGE_HEIGHT * (googleBtnLayout.scale - 1)}px`,
                                    }
                                    : legacyStyle({ transform: 'scale(2)', display: 'inline-block', marginTop: '5%' })}
                            >
                                <GoogleLogin
                                    onSuccess={handleLoginSuccess}
                                    onError={handleLoginError}
                                    disabled={loading}
                                    size={googleBtnLayout != null ? 'large' : undefined}
                                    width={googleBtnLayout != null ? String(googleBtnLayout.renderWidth) : undefined}
                                />
                            </div>
                        )}

                        <div
                            className="login-with-google__intent-toggle"
                            role="group"
                            aria-label="Account type"
                        >
                            <button
                                type="button"
                                className={`login-with-google__intent-btn${accountIntent === 'student' ? ' login-with-google__intent-btn--active' : ''}`}
                                onClick={() => setAccountIntent('student')}
                            >
                                I&apos;m a student
                            </button>
                            <button
                                type="button"
                                className={`login-with-google__intent-btn${accountIntent === 'teacher' ? ' login-with-google__intent-btn--active' : ''}`}
                                onClick={() => setAccountIntent('teacher')}
                            >
                                I&apos;m a teacher
                            </button>
                        </div>

                        {showRememberMe && (
                            <div className={isThemed ? 'login-with-google__remember-wrap' : 'mt-3'}>
                                <div className={isThemed ? 'login-with-google__remember' : 'form-check'}>
                                    <input
                                        className={isThemed ? 'login-with-google__remember-input' : 'form-check-input'}
                                        type="checkbox"
                                        id="rememberMe"
                                        checked={rememberMe}
                                        onChange={(e) => setRememberMe(e.target.checked)}
                                    />
                                    <label
                                        className={isThemed ? 'login-with-google__remember-label' : 'form-check-label'}
                                        htmlFor="rememberMe"
                                    >
                                        Remember me
                                    </label>
                                </div>
                            </div>
                        )}

                        <p className="login-with-google__manual-links mb-0">
                            <Link to="/login/email" state={authNavState}>Sign in with email</Link>
                            {' · '}
                            <Link to="/register" state={authNavState}>Create account</Link>
                        </p>
                        {isJoinFlow && (
                            <p className="login-with-google__join-hint text-muted mt-3 mb-0">
                                After signing in, you&apos;ll continue to secure checkout for your one-year membership.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
